import {
  AltitudeCalibrationDto,
  AltitudeResponseDto,
  ArrowPositionModel,
  ClockMechanism_ManualMehMoveModel,
  DevicesService,
  HandsCorrection_HandsCorrectionMode,
  HandsCorrectionItemDto,
  HandsCorrectionStateDto,
  HandsDeviationAlertDto,
  HandsDeviationItemDto,
  MechanismService,
  PressureCalibrationHistoryDto,
  SaveAltitudeRequest,
  SetHandsAfterLearningModeRequest,
  SetHandsCorrectionModeRequest,
  StandardTimeModel,
  StateModel,
} from "../api";
import {MechanismParams, MeteoData} from "../models/Mechanism.model";
import {mapManualModeToContract, mapTimeValue} from "../shared/Helpers.utils";

export const MechanismApi = {
  // Получение времени устройства
  getTime: async (id: string) => {
    return DevicesService.getApiDevicesTime(id);
  },

  // Сохранение времени устройства
  saveTime: async (id: string, time: string | Date): Promise<boolean> => {
    const payload: StandardTimeModel = {
      time: mapTimeValue(time),
    };

    await DevicesService.postApiDevicesTime(id, payload);
    return true;
  },

  // Получение параметров механизма
  getMechanismParams: async (id: string): Promise<MechanismParams & Record<string, unknown>> => {
    const response = await MechanismService.getApiMechanismParams(id);
    const responseRecord = response as Record<string, unknown>;

    return {
      ...response,
      arrowPosition: response.arrowPosition
        ? {
            hour: response.arrowPosition.hour ?? 0,
            minute: response.arrowPosition.minute ?? 0,
          }
        : undefined,
      currentAltitude: (responseRecord.currentAltitude as number | null | undefined) ?? null,
      savedAltitude: (responseRecord.savedAltitude as number | null | undefined) ?? null,
    };
  },

  // Управление движением
  setMovementPermission: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await MechanismService.postApiMechanismMovement(id, payload);
    return true;
  },

  // Управление режимом
  setAutoMode: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await MechanismService.postApiMechanismMode(id, payload);
    return true;
  },

  // Установка положения стрелок
  setArrowsPosition: async (id: string, hoursPosition: number | string, minutesPosition: number | string): Promise<boolean> => {
    const payload: ArrowPositionModel = {
      hoursPosition: Number(hoursPosition),
      minutesPosition: Number(minutesPosition),
    };

    await MechanismService.postApiMechanismArrows(id, payload);
    return true;
  },

  // Получение метеоданных
  getMechanismMeteoData: async (id: string): Promise<MeteoData> => {
    const response = await MechanismService.getApiMechanismMeteo(id);

    return {
      temperature: response.temperature ?? 0,
      pressure: response.pressure ?? 0,
      humidity: response.humidity ?? 0,
    };
  },

  // Получение импульса
  getMechanismImpulse: async (id: string) => {
    return MechanismService.getApiMechanismImpulseLive(id);
  },

  // Ручной подгон механизма
  manualMovement: async (id: string, mode: number): Promise<boolean> => {
    const payload: ClockMechanism_ManualMehMoveModel = {
      mode: mapManualModeToContract(mode),
    };

    await MechanismService.postApiMechanismMovementManual(id, payload);
    return true;
  },

  setStopTimer: async (id: string, stopTimer: number): Promise<void> => {
    await MechanismService.postApiMechanismStopTimer(id, {stopTimer});
  },

  // Поиск механизма
  searchMechanism: async (id: string): Promise<boolean> => {
    await MechanismService.postApiMechanismSearch(id);
    return true;
  },

  // Расчёт автоматической высоты по давлению
  calculateAltitude: async (id: string): Promise<AltitudeResponseDto> => {
    return MechanismService.postApiMechanismAltitudeCalculate(id);
  },

  // Сохранение ручной высоты и калибровка датчика давления
  saveAltitude: async (id: string, altitude: number): Promise<AltitudeCalibrationDto> => {
    const payload: SaveAltitudeRequest = {altitude};
    return MechanismService.putApiMechanismAltitudeSave(id, payload);
  },

  // История сохранений ручной высоты (накопительная калибровка датчика давления)
  getAltitudeCalibrations: async (id: string): Promise<PressureCalibrationHistoryDto> => {
    return MechanismService.getApiMechanismAltitudeCalibrations(id);
  },

  // Сбросить все сохранения ручной высоты и поправку датчика
  resetAltitudeCalibrations: async (id: string): Promise<PressureCalibrationHistoryDto> => {
    return MechanismService.deleteApiMechanismAltitudeCalibrations(id);
  },

  // Режим автокорректировки стрелок и данные датчиков шестерён
  getHandsCorrection: async (id: string): Promise<HandsCorrectionStateDto> => {
    return MechanismService.getApiMechanismHandsCorrection(id);
  },

  // Сменить режим автокорректировки стрелок
  setHandsCorrectionMode: async (id: string, mode: HandsCorrection_HandsCorrectionMode): Promise<HandsCorrectionStateDto> => {
    const payload: SetHandsCorrectionModeRequest = {mode};
    return MechanismService.putApiMechanismHandsCorrectionMode(id, payload);
  },

  // Режим, который система включит сама после обучения: Notify (по умолчанию) или Auto
  setHandsAfterLearningMode: async (id: string, mode: HandsCorrection_HandsCorrectionMode): Promise<HandsCorrectionStateDto> => {
    const payload: SetHandsAfterLearningModeRequest = {mode};
    return MechanismService.putApiMechanismHandsCorrectionAfterLearningMode(id, payload);
  },

  // История корректировок стрелок (новые сверху)
  getHandsCorrectionHistory: async (id: string, limit = 100): Promise<HandsCorrectionItemDto[]> => {
    return MechanismService.getApiMechanismHandsCorrectionHistory(id, limit);
  },

  // Сбросить сбои: красные строки скрываются, история сохраняется (только администратор)
  dismissHandsDeviations: async (id: string): Promise<HandsCorrectionStateDto> => {
    return MechanismService.postApiMechanismHandsCorrectionDeviationsDismiss(id);
  },

  // История сбоев датчиков и подгонов (только администратор)
  getHandsCorrectionDeviations: async (id: string, limit = 100): Promise<HandsDeviationItemDto[]> => {
    return MechanismService.getApiMechanismHandsCorrectionDeviations(id, limit);
  },

  getHandsDeviationAlerts: async (controlBlockId: string): Promise<HandsDeviationAlertDto[]> => {
    return MechanismService.getApiMechanismHandsCorrectionAlerts(controlBlockId);
  },

  getAllHandsDeviationAlerts: async (): Promise<HandsDeviationAlertDto[]> => {
    return MechanismService.getApiMechanismHandsCorrectionAlerts1();
  },
};

export default MechanismApi;
